import SharedDialog from '../../../shared/shared-dialog';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useState } from 'react';
import { ExtendedVaultExportModel } from './vault-export-grid';
import { getDeletePermissionForCorpus } from '../../../utils/vault-utils';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useVaultActions } from './hooks/use-vault-actions';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface DeleteVaultExportsDialogProps {
  open: boolean;
  matterId: string;
  handleClose: () => void;
  selectedExportIds: string[];
  allExports: ExtendedVaultExportModel[];
  setSelectedExports: (exports: string[]) => void;
}

export default function DeleteVaultExportsDialog({
  open,
  matterId,
  handleClose,
  selectedExportIds,
  allExports,
  setSelectedExports,
}: DeleteVaultExportsDialogProps) {
  const { changeLogRoles, toolsRoles } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { deleteExports, isDeletingExports } = useVaultActions();

  const selectedExports = allExports.filter((exp) =>
    selectedExportIds.includes(exp.id)
  );

  const performDelete = () => {
    const groups: Record<string, string[]> = {};
    selectedExports.forEach((exp) => {
      const mId = exp.matterId || matterId;
      if (mId) {
        if (!groups[mId]) {
          groups[mId] = [];
        }
        groups[mId].push(exp.id);
      }
    });

    const entries = Object.entries(groups);
    let completed = 0;

    const checkAndComplete = () => {
      completed++;
      if (completed === entries.length) {
        setOpenChangeLogDialog(false);
        setChangeLogMessage('');
        setSelectedExports([]);
        handleCloseDialog();
      }
    };

    if (entries.length === 0) {
      handleCloseDialog();
      return;
    }

    entries.forEach(([mId, ids]) => {
      deleteExports(
        { matterId: mId, selectedExportIds: ids, changeLogMessage },
        {
          onSuccess: checkAndComplete,
        }
      );
    });
  };

  const handleSubmit = () => {
    const hasGlobalChangelog = isAbleToSeeUIElement(
      changeLogRoles,
      VaultRoles.DeleteVaultExports.value
    );

    const hasSpecificChangelog = selectedExports.some((exp) => {
      const permission = getDeletePermissionForCorpus(exp.corpus);
      if (!permission) return false;

      const hasToolPermission = isAbleToSeeUIElement(
        toolsRoles,
        permission.value
      );
      const hasChangelogPermission = isAbleToSeeUIElement(
        changeLogRoles,
        permission.value
      );

      return hasToolPermission && hasChangelogPermission;
    });

    if (hasGlobalChangelog || hasSpecificChangelog) {
      setOpenChangeLogDialog(true);
    } else {
      performDelete();
    }
  };

  const handleCloseDialog = () => {
    handleClose();
  };

  const isMultiple = selectedExports.length > 1;

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleCloseDialog}
        title={isMultiple ? 'Delete Exports' : 'Delete Export'}
        maxWidth="sm"
        dialogContent={
          <Box>
            <Typography>
              {isMultiple ? (
                <>
                  Delete <strong>{selectedExports.length} exports</strong>?
                </>
              ) : (
                <>
                  Delete <strong>{selectedExports[0]?.name}</strong>?
                </>
              )}
            </Typography>
          </Box>
        }
        primaryButton={{
          title: isMultiple ? 'Delete Exports' : 'Delete Export',
          action: () => handleSubmit(),
          disabled: isDeletingExports,
          loading: isDeletingExports,
          color: 'error',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          performDelete();
        }}
        mutateLoading={isDeletingExports}
      />
    </>
  );
}
